/**
 * 용어 사전 SSOT — pion-knowledge 전반에서 쓰는 개념을 한곳에 모은다.
 *
 * 용도:
 *  - docs-site `<Term>` / `<Glossary>` 컴포넌트가 이 테이블을 참조한다
 *  - dashboard 도 동일 테이블을 import 해 라벨 툴팁에 재사용 (DRY)
 *  - 용어를 추가/수정할 땐 반드시 이 파일만 수정 — 컴포넌트는 건드리지 않는다
 *
 * `href` 는 `/docs/concepts/glossary` 의 앵커. 호버 팝오버 안 "자세히" 링크가 이걸 쓴다.
 * `aliases` 는 영문/축약/다른 표기 — `<Term name="slice">슬라이스</Term>` 처럼
 * 어떤 alias 를 써도 동일 항목으로 해석되도록 lookup 키 역할.
 */
export interface GlossaryTerm {
	/** 표시용 정식 명칭 (한국어 우선) */
	readonly label: string
	/** 한 문장 정의 — 호버 시 바로 보이는 부분 */
	readonly summary: string
	/** 선택: 긴 설명 (마크다운 허용). glossary 페이지에서만 렌더 */
	readonly details?: string
	/** 관련 용어 id — glossary 페이지 cross-link */
	readonly related?: ReadonlyArray<string>
	/** lookup alias — 영문 / 한글 / 축약. 소문자 정규화해 매칭 */
	readonly aliases?: ReadonlyArray<string>
}

/**
 * Map key = 용어 id (kebab-case 영문). `<Term name="slice">` 의 `name` 이 이 키.
 * 값이 없으면 아무것도 렌더하지 않고 children 만 그대로 보여준다 (lint 안전).
 */
export const GLOSSARY = {
	slice: {
		label: "슬라이스",
		summary:
			"한 테넌트가 저장한 최종 온톨로지 규격. 슬라이스 밖 kind / relation / shape 를 가진 데이터는 knowledge_upsert 에서 400 으로 거부된다.",
		aliases: ["슬라이스", "slice", "ontology-slice", "OntologySlice"],
		related: ["pack", "preset", "block", "shape", "kind", "relation"]
	},
	block: {
		label: "블럭",
		summary: "최소 단위 — 1 kind 한 덩어리 (예: person, service, artifact).",
		aliases: ["블럭", "block", "ontology-block"],
		related: ["pack", "slice", "kind"]
	},
	pack: {
		label: "팩",
		summary: "도메인별 블럭 묶음 (예: core-org, engineering, literature). 여러 팩을 합쳐 슬라이스를 만든다.",
		aliases: ["팩", "pack", "ontology-pack"],
		related: ["block", "preset", "slice"]
	},
	preset: {
		label: "프리셋",
		summary: "팩들의 추천 조합 — 조직 전체 스타터 단축키 (예: 풀스택 팀, SRE 팀).",
		aliases: ["프리셋", "preset", "ontology-preset"],
		related: ["pack", "slice"]
	},
	kind: {
		label: "kind",
		summary: "노드의 종류 라벨 (예: person / place / artifact). 블럭 1 개 = kind 1 개.",
		aliases: ["kind", "node-kind", "종류"],
		related: ["block", "shape"]
	},
	relation: {
		label: "관계",
		summary: "두 노드를 잇는 엣지의 라벨 (예: VISITED, OWNED_BY). 슬라이스의 shape 가 허용 조합을 결정한다.",
		aliases: ["relation", "관계", "edge-relation"],
		related: ["shape", "edge"]
	},
	shape: {
		label: "shape",
		summary: "허용되는 (sourceKind → relation → targetKind) 트리플. 슬라이스가 정의한다.",
		aliases: ["shape", "triple", "트리플"],
		related: ["slice", "kind", "relation"]
	},
	entity: {
		label: "엔티티",
		summary: "그래프 위의 '사물' 하나 — 사람/장소/조직/아티팩트 같은 노드.",
		aliases: ["entity", "엔티티", "node", "노드"],
		related: ["kind", "edge"]
	},
	edge: {
		label: "엣지",
		summary: "두 엔티티 사이의 관계 하나 — from / relation / to 로 표현된다.",
		aliases: ["edge", "엣지", "link", "연결"],
		related: ["entity", "relation", "shape"]
	},
	episode: {
		label: "에피소드",
		summary:
			"언제 · 누가 · 무엇을 · 왜 를 담은 시간축 기록. 여러 엣지가 한 에피소드에 묶여 '왜 이 관계인가' 의 근거가 된다.",
		aliases: ["episode", "에피소드"],
		related: ["edge", "as-of"]
	},
	tenant: {
		label: "테넌트",
		summary:
			"격리 단위. serviceSlug 로 식별되며 모든 쿼리/쓰기에 강제 주입된다 — 옆 테넌트의 데이터는 보이지도 써지지도 않는다.",
		aliases: ["tenant", "테넌트", "service-slug", "serviceSlug"],
		related: ["slice"]
	},
	rag: {
		label: "RAG",
		summary:
			"Retrieval-Augmented Generation — 검색으로 찾은 문서/사실을 LLM 응답에 직접 주입해 환각을 줄이는 기법.",
		aliases: ["rag", "retrieval-augmented-generation"],
		related: ["kg", "embedding"]
	},
	kg: {
		label: "지식 그래프 (KG)",
		summary: "노드와 엣지로 세계를 표현한 구조. pion-knowledge 는 KG + 벡터를 함께 쓰는 하이브리드 RAG.",
		aliases: ["kg", "knowledge-graph", "지식그래프"],
		related: ["rag", "embedding"]
	},
	embedding: {
		label: "임베딩",
		summary: "텍스트를 의미 벡터로 변환한 값. 의미가 가까운 문장은 벡터 공간에서 가깝다.",
		aliases: ["embedding", "임베딩", "vector-embedding"],
		related: ["vector-search", "rag"]
	},
	"vector-search": {
		label: "벡터 검색",
		summary: "임베딩 간 거리로 '의미가 가까운' 문서/엣지를 찾는다 — 키워드가 안 겹쳐도 매칭.",
		aliases: ["vector-search", "semantic-search", "벡터검색", "시맨틱검색"],
		related: ["embedding", "rag"]
	},
	traverse: {
		label: "traverse",
		summary: "시작 노드에서 엣지를 따라 이웃을 넓혀가는 탐색 — '이 사건에서 영향 받는 것들' 같은 질문.",
		aliases: ["traverse", "graph-traverse", "탐색"],
		related: ["edge", "entity"]
	},
	invalidate: {
		label: "무력화",
		summary:
			"관계/엣지/에피소드를 철회해 특정 시점 이후 비활성으로 만든다 — 이력은 남지만 현재 쿼리에는 잡히지 않음.",
		aliases: ["invalidate", "무력화", "철회"],
		related: ["as-of", "edge", "episode"]
	},
	"as-of": {
		label: "asOf (시점 질의)",
		summary: "특정 시각 기준으로 그래프 상태를 조회한다. 무력화된 엣지도 그 시점 이전이면 보인다.",
		aliases: ["as-of", "asof", "시점"],
		related: ["invalidate", "episode"]
	},
	violation: {
		label: "슬라이스 위반",
		summary: "슬라이스가 허용하지 않는 kind / relation / shape 를 가진 데이터. 기본 정책으로 400 거부.",
		aliases: ["violation", "위반", "slice-violation"],
		related: ["slice", "shape"]
	},
	"knowledge-upsert": {
		label: "knowledge_upsert",
		summary: "MCP 도구. 새 엔티티/관계를 KG 에 넣는다. 슬라이스 검증을 거쳐 위반이 있으면 400 으로 거부.",
		aliases: ["knowledge-upsert", "upsert"],
		related: ["slice", "violation"]
	},
	mcp: {
		label: "MCP",
		summary:
			"Model Context Protocol — LLM 에이전트가 외부 도구(knowledge_upsert, kg_search 등)를 호출하는 표준 프로토콜.",
		aliases: ["mcp", "model-context-protocol"],
		related: ["knowledge-upsert"]
	},
	alias: {
		label: "동의어 (alias)",
		summary:
			"한 relation (예: MEMBER_OF) 에 등록된 동의어. LLM 이 AFFILIATED_WITH 를 뱉으면 canonical 인 MEMBER_OF 로 정규화된다. KG 에는 canonical 만 저장.",
		aliases: ["alias", "동의어", "relation-alias"],
		related: ["relation", "knowledge-upsert", "violation"]
	},
	"transaction-id": {
		label: "transactionId",
		summary:
			"1 client HTTP request = 1 transactionId. 같은 transactionId 의 모든 LLM 호출은 CloudWatch 한 LogStream (YYYY/MM/DD/{transactionId}) 에 묶인다.",
		details:
			"`x-request-id` 헤더가 제공되면 sanitize (영숫자 `._:-` 최대 128자) 후 그대로 사용, 없으면 UUID v4 발급. **호출자가 보낸 헤더 값이 그대로 LogStream 이름이 될 수 있으니** transactionId 를 외부로 공유할 때는 PII / 비밀 비포함을 확인할 책임이 호출측에 있다.",
		aliases: ["transaction-id", "transactionId", "tx-id"],
		related: ["request-id", "tenant"]
	},
	"request-id": {
		label: "requestId",
		summary:
			"LLM 호출 1 건마다 새로 발급되는 UUID. 1 transactionId 안에 N 개 requestId 가 들어간다 (예: knowledge_search 1 호출 = embedding N 개 + generate 1 개).",
		aliases: ["request-id", "requestId"],
		related: ["transaction-id"]
	},
	"prompt-hash": {
		label: "promptHash",
		summary:
			"system prompt 의 djb2 32-bit 해시. 원문 저장 없이 prompt 버전 변동을 추적한다 — 보안/무결성 증빙 용도가 아니다.",
		details:
			"ADR 0021 (원문 저장 금지) 의 우회로. **djb2 는 비암호학 해시이므로 충돌 가능 + 짧은 prompt 에 대해서는 brute-force 로 원문 추정이 가능**. 변경 *감지* 신호로만 사용하고 감사 signature 처럼 신뢰하지 말 것. system prompt 가 바뀌면 모든 promptHash 가 일괄 회전하므로 prompt 빌더 변경 시 외부 트래커에 사전 공지.",
		aliases: ["prompt-hash", "promptHash"],
		related: ["transaction-id"]
	},
	"cache-read": {
		label: "cacheRead / cacheWrite",
		summary:
			"Anthropic prompt caching 의 토큰 카운트. cacheRead 는 cache hit (× 0.1 단가), cacheWrite 는 cache 신규 적재 (1h TTL = × 2.0 단가).",
		details:
			"`LLMLogEntry.inputTokens.total` 은 uncached + cacheRead + cacheWrite 합계로 그대로 적재된다 (provider 가 분리해 주면). 비용 계산 시 차등 단가 적용 필요 — 단순 `inputTokens.total × input_rate` 은 cache 활성 환경에서 ±50% 괴리.",
		aliases: ["cache-read", "cache-write", "cacheRead", "cacheWrite", "prompt-cache-tokens"],
		related: ["transaction-id"]
	},
	"hint-source": {
		label: "hint_source",
		summary:
			"Phase 4-staging 의 candidate 출처 라벨. 4종: co-occurrence / embedding-similarity / llm-inferred / manual. 본체 `node_associations.source` 와 1:1 매핑되지만 staging 은 미검수 단계라 의도적으로 다른 컬럼명을 쓴다.",
		aliases: ["hint-source", "hintSource", "hint_source"],
		related: ["candidate-weight"]
	},
	"candidate-weight": {
		label: "candidate_weight",
		summary:
			"Phase 4-staging 의 추출 신뢰도 (0~1). 운영자 검수 후 promote 시 본체 `node_associations.weight` 로 그대로 옮겨진다. weight 와 달리 미검수 가설 단계라는 의도가 컬럼명에 박혀 있다.",
		aliases: ["candidate-weight", "candidateWeight", "candidate_weight"],
		related: ["hint-source"]
	},
	"api-key": {
		label: "API 키",
		summary:
			"Bearer 토큰. 발급 시점 응답 1 회만 평문 노출되고 DB 에는 scrypt 해시만 저장된다. 서버 단위로 발급 — 같은 테넌트라도 다른 서버는 다른 키.",
		details:
			"키 형식은 `pk_live_...` 또는 `pk_test_...`. 잃어버리면 회수 불가 — 신규 발급만 가능. revoke 시 `revokedAt` 타임스탬프 세팅 (DELETE 없음).",
		aliases: ["api-key", "apiKey", "키", "bearer", "토큰"],
		related: ["scope", "mcp-server", "tenant"]
	},
	scope: {
		label: "scope",
		summary:
			"API 키 권한 매트릭스. `kg:read` / `kg:write` / `docs:read` / `docs:write` / `episodes:read` / `episodes:write` 등 `{resource}:{action}` 포맷. 키마다 ReadonlyArray<scope>.",
		details:
			"MCP 도구마다 `requiredScopes` 가 OR 매트릭스로 정의됨 — 키의 scopes 가 이 중 하나만 만족해도 호출 가능. 도구별 scope 매트릭스는 reference/mcp-tools 참조.",
		aliases: ["scope", "권한", "scopes"],
		related: ["api-key", "tool"]
	},
	"mcp-server": {
		label: "MCP 서버",
		summary:
			"한 테넌트 안의 도구 묶음 단위. URL `/mcp/{slug}/{server}` 의 두 번째 segment. 같은 테넌트라도 reader / agent / docs 등 여러 서버를 만들어 서로 다른 enabledTools + 키 묶음으로 분리할 수 있다.",
		details:
			"운영 패턴: 'reader (kg:read 전용)' + 'agent (kg:read + kg:write + docs:read)' 같이 권한별 서버를 나눈다. 서버 enabledTools 와 키 scope 의 2 중 게이트가 호출 인가를 결정.",
		aliases: ["mcp-server", "mcpServer", "서버"],
		related: ["api-key", "scope", "tool", "tenant"]
	},
	"service-slug": {
		label: "serviceSlug",
		summary:
			"테넌트 식별자. URL `/mcp/{slug}/...` 의 첫 segment + DB 모든 행의 격리 키. 한 번 정한 slug 는 바꿀 수 없음 — 키 형식: `[a-z0-9][a-z0-9-]{0,62}[a-z0-9]`.",
		aliases: ["service-slug", "serviceSlug", "slug"],
		related: ["tenant"]
	},
	tool: {
		label: "MCP 도구 (tool)",
		summary:
			"MCP 호출 단위 — `knowledge_search` / `knowledge_traverse` / `knowledge_upsert` / `edge_*` / `docs_*` 등. `tools/call` 의 params.name 으로 지정.",
		details:
			"카탈로그는 `src/kernel/routes/mcp-tools.ts` 의 `TOOL_CATALOG` SSOT. 각 도구는 `requiredScopes` 와 `inputSchema` 를 갖고, 서버 enabledTools 화이트리스트 + 키 scope 양 게이트 통과 시에만 호출.",
		aliases: ["tool", "mcp-tool", "도구"],
		related: ["mcp-server", "scope"]
	},
	oauth: {
		label: "OAuth 2.1 / Black OAuth",
		summary:
			"두 가지 OAuth 가 공존. (1) `/admin/auth` 는 Black OAuth — 관리자 대시보드 세션. (2) `/oauth/*` 는 OAuth 2.1 AS — Claude Connectors 가 API 키를 자동 발급받는 표준 플로우.",
		details:
			"외부 개발자가 직접 만질 일은 거의 없음. Claude Desktop / Cursor 가 자동으로 `/oauth/register` (DCR) → `/oauth/authorize` (consent) → `/oauth/token` (PKCE) 를 거쳐 `pk_...` 키를 받음. 결과 키는 manual 발급 키와 동일한 `apiKeys` 테이블에 저장.",
		aliases: ["oauth", "oauth2", "oauth2.1", "black-oauth"],
		related: ["api-key", "mcp-server"]
	},
	"json-rpc": {
		label: "JSON-RPC 2.0",
		summary:
			"MCP 가 사용하는 호출 포맷. body `{jsonrpc:'2.0', id, method, params}`. method 는 보통 `tools/list` 또는 `tools/call`. 응답은 `{jsonrpc:'2.0', id, result}` 또는 `{jsonrpc:'2.0', id, error}`.",
		aliases: ["json-rpc", "jsonRpc", "jsonrpc"],
		related: ["mcp", "mcp-server", "tool"]
	}
} as const satisfies Record<string, GlossaryTerm>

export type GlossaryId = keyof typeof GLOSSARY

/**
 * alias 또는 id 로 용어를 조회. 소문자 정규화해 매칭.
 * 존재하지 않으면 undefined — 호출측은 fallback 으로 children 을 그대로 렌더하면 된다.
 */
export function lookupTerm(nameOrAlias: string): (GlossaryTerm & { readonly id: string }) | undefined {
	const q = nameOrAlias.trim().toLowerCase()
	if (!q) return undefined
	for (const [id, term] of Object.entries(GLOSSARY) as [GlossaryId, GlossaryTerm][]) {
		if (id.toLowerCase() === q) return { ...term, id }
		if (term.aliases?.some((a) => a.toLowerCase() === q)) return { ...term, id }
	}
	return undefined
}

/** glossary 페이지 렌더용 — id 기준 정렬된 엔트리 배열. */
export function listTerms(): ReadonlyArray<GlossaryTerm & { readonly id: string }> {
	return (Object.entries(GLOSSARY) as [GlossaryId, GlossaryTerm][])
		.map(([id, term]) => ({ ...term, id }))
		.sort((a, b) => a.id.localeCompare(b.id))
}
